@tailwind base;
@tailwind components;
@tailwind utilities;

@layer base {
  :root {
    /* Cinema Light Palette (Clean Architectural Alabaster) */
    --cinema-950: 246 248 251; /* Canvas `#f6f8fb` */
    --cinema-900: 255 255 255; /* Cards / panels `#ffffff` */
    --cinema-850: 241 245 249; /* Elevated cards `#f1f5f9` */
    --cinema-800: 226 232 240; /* Interactive surfaces `#e2e8f0` */
    --cinema-750: 211 220 230; /* Secondary surface `#d3dce6` */
    --cinema-700: 186 198 214; /* Structural hairline borders `#bac6d6` */
    --cinema-600: 140 155 175; /* Muted borders `#8c9baf` */
    --cinema-500: 100 116 139; /* Secondary copy `#64748b` (4.8:1) */
    --cinema-400: 71 85 105;   /* Body text `#475569` (7.6:1) */
    --cinema-300: 51 65 85;    /* Strong labels `#334155` (9.5:1) */
    --cinema-200: 30 41 59;    /* Subheadings `#1e293b` (12.8:1) */
    --cinema-100: 15 23 42;    /* Primary dark text `#0f172a` (17.8:1) */

    --lamp-default: 217 119 6; /* Rich amber `#d97706` */
    --lamp-bright: 180 83 9;
    --lamp-soft: 254 243 199;
    --lamp-dim: rgba(217, 119, 6, 0.12);
    --lamp-glow: rgba(217, 119, 6, 0.22);

    --reel-cyan: 2 132 199;
    --reel-emerald: 5 150 105;
    --reel-rose: 225 29 72;

    --shadow-cinema-screen: 0 16px 48px -12px rgba(15, 23, 42, 0.14), 0 0 30px -5px rgba(217, 119, 6, 0.08);
    --shadow-lamp-glow: 0 0 25px rgba(217, 119, 6, 0.2);
    --shadow-panel: 0 4px 18px -2px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04);

    --border-subtle: rgba(15, 23, 42, 0.09);
    --border-focus: rgba(217, 119, 6, 0.5);
    --grid-line: rgba(15, 23, 42, 0.04);
  }

  .dark {
    /* Cinema Dark Palette (Inky Auditorium OLED) */
    --cinema-950: 6 7 10;     /* Inky auditorium black `#06070a` */
    --cinema-900: 12 14 20;   /* Chassis baseline `#0c0e14` */
    --cinema-850: 17 20 29;   /* Card surface `#11141d` */
    --cinema-800: 24 28 40;   /* Elevated deck `#181c28` */
    --cinema-750: 31 36 52;   /* Secondary surface `#1f2434` */
    --cinema-700: 43 51 70;   /* Hairline structural border `#2b3346` */
    --cinema-600: 61 71 95;   /* Muted stroke `#3d475f` */
    --cinema-500: 86 99 128;  /* Secondary copy `#566380` */
    --cinema-400: 133 147 174;/* Slate text `#8593ae` */
    --cinema-300: 180 192 212;/* Off-white label `#b4c0d4` */
    --cinema-200: 220 228 240;/* Strong text `#dce4f0` */
    --cinema-100: 241 245 250;/* Crisp high-contrast white `#f1f5fa` */

    --lamp-default: 230 162 60; /* Warm 3200K tungsten `#e6a23c` */
    --lamp-bright: 247 190 94;
    --lamp-soft: 251 226 168;
    --lamp-dim: rgba(230, 162, 60, 0.12);
    --lamp-glow: rgba(230, 162, 60, 0.3);

    --reel-cyan: 56 189 248;
    --reel-emerald: 16 185 129;
    --reel-rose: 244 63 94;

    --shadow-cinema-screen: 0 20px 60px -15px rgba(0, 0, 0, 0.9), 0 0 40px -10px rgba(230, 162, 60, 0.08);
    --shadow-lamp-glow: 0 0 30px rgba(230, 162, 60, 0.25);
    --shadow-panel: 0 4px 20px -2px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.05);

    --border-subtle: rgba(255, 255, 255, 0.07);
    --border-focus: rgba(230, 162, 60, 0.4);
    --grid-line: rgba(255, 255, 255, 0.02);
  }

  * {
    box-sizing: border-box;
  }

  html {
    font-feature-settings: 'cv02', 'cv03', 'cv04', 'cv11';
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: rgb(var(--cinema-950));
    color: rgb(var(--cinema-100));
  }

  body {
    margin: 0;
    min-height: 100vh;
    overflow-x: hidden;
    background-color: rgb(var(--cinema-950));
    color: rgb(var(--cinema-100));
    -webkit-tap-highlight-color: transparent;
  }

  /* Precise cinema scrollbars */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: rgb(var(--cinema-700) / 0.5);
    border-radius: 3px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: rgb(var(--cinema-600));
  }

  /* Optical focus defaults */
  :focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgb(var(--lamp-default));
  }
}

@layer utilities {
  .subtle-grid {
    background-size: 32px 32px;
    background-image: 
      linear-gradient(to right, var(--grid-line) 1px, transparent 1px),
      linear-gradient(to bottom, var(--grid-line) 1px, transparent 1px);
  }

  .cinema-border {
    border: 1px solid var(--border-subtle);
  }

  .cinema-border-focus {
    border: 1px solid var(--border-focus);
  }

  .hairline-b {
    border-bottom: 1px solid var(--border-subtle);
  }

  .hairline-t {
    border-top: 1px solid var(--border-subtle);
  }

  .hairline-l {
    border-left: 1px solid var(--border-subtle);
  }

  .hairline-r {
    border-right: 1px solid var(--border-subtle);
  }
}
/* Scoped Video Player White Text Enforcement */
.always-white {
  color: #ffffff;
}

/* Video Player Overrides */
.video-player-container,
.youtube-player-container {
  position: relative;
  background: #000000;
  overflow: hidden;
}

.video-player-container video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.youtube-player-container iframe {
  width: 100% !important;
  height: 100% !important;
  border: 0;
}

.player-controls-layer {
  transition: opacity 0.22s cubic-bezier(0.16, 1, 0.3, 1), transform 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

.player-controls-layer.controls-hidden {
  opacity: 0;
  pointer-events: none;
  transform: translateY(6px);
}

.film-track {
  position: relative;
  height: 6px;
  cursor: pointer;
  transition: height 0.12s ease;
}

.film-track:hover {
  height: 9px;
}

.film-track-thumb {
  width: 13px;
  height: 13px;
  border-radius: 2px;
  background: #f7be5e;
  box-shadow: 0 0 10px rgba(230, 162, 60, 0.6);
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%) scale(0);
  transition: transform 0.12s ease;
  pointer-events: none;
}

.film-track:hover .film-track-thumb {
  transform: translate(-50%, -50%) scale(1);
}

/* Precision Subtitles */
.cinema-subtitle {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-weight: 500;
  letter-spacing: 0.01em;
  text-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.95),
    0 0 12px rgba(0, 0, 0, 0.9),
    0 1px 0 #000;
  white-space: pre-wrap;
}

/* Contain scroll in modals and sidebars */
[role="dialog"], .modal-body, .contain-overscroll {
  overscroll-behavior: contain;
}

/* Accessible Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
